import {create} from "zustand";
import {KeyHistoryRecord, KeyLocation, KeySnapshot, KeyTransaction, KeyVaultRef} from "./Model";
import {useCurrentOfficeStore} from "./CurrentOfficeStore";
import api from "../util/api";
import {keyTransactionSubscription} from "./KeyTransaction";

export interface KeyTransactionRecord {
    id: string;
    timestamp: Date;
    oldSnapshot: KeySnapshot;
    newRecord: KeyHistoryRecord;
}

interface KeyTransactionState {
    execute(transactions: { keySnapshot: KeySnapshot, tx: KeyTransaction }[]): Promise<KeyTransactionRecord[]>;
    rollback(record: KeyTransactionRecord): Promise<void>;
}

export function patchKey(
    keySnapshot: KeySnapshot,
    date: string,
    {
        vault = keySnapshot.historyRecord.vault,
        location = keySnapshot.historyRecord.location,
        notes = '',
    }: {
        vault?: KeyVaultRef,
        location?: KeyLocation,
        notes?: string,
    }
): { keySnapshot: KeySnapshot, tx: KeyTransaction } {
    return {
        keySnapshot,
        tx: {
            keyNumber: keySnapshot.key.keyNumber,
            vault: vault,
            date,
            newLocation: location,
            notes: notes,
        },
    }
}

export const useKeyTransactionStore = create<KeyTransactionState>(() => ({
    async execute(transactions: { keySnapshot: KeySnapshot, tx: KeyTransaction }[]): Promise<KeyTransactionRecord[]> {
        const currentOfficeId = useCurrentOfficeStore.getState().officeId;
        if (currentOfficeId === null) {
            throw new Error('No current office');
        }

        const response = await api
            .post<KeyHistoryRecord[]>(
                `/offices/${currentOfficeId}/key-transactions`,
                transactions.map(({tx}) => tx),
            );
        const createdRecords: KeyHistoryRecord[] = response.data;
        const timestamp = new Date();

        const newRecords = transactions
            .map(({keySnapshot}, index) => ({
                id: crypto.randomUUID(),
                timestamp,
                oldSnapshot: keySnapshot,
                newRecord: createdRecords[index],
            }));

        keyTransactionSubscription.notify();

        return newRecords;
    },
    async rollback(record: KeyTransactionRecord) {
        const currentOfficeId = useCurrentOfficeStore.getState().officeId;
        if (currentOfficeId === null) {
            throw new Error('No current office');
        }

        await api.post(`/offices/${currentOfficeId}/key-transactions/rollback`, {
            keyId: record.oldSnapshot.key.id,
            createdRecordId: record.newRecord.id,
            closedRecordId: record.oldSnapshot.historyRecord.id,
        });

        keyTransactionSubscription.notify();
    }
}));
